/**
 * ConversationsView — BUB-135 / BUB-141
 * Browse Paul ↔ customer conversations; all rows open full-chat drawer.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Flex,
  Grid,
  Input,
  Select,
  Switch,
  Typography,
} from "antd";
import {
  MessageOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ConversationDrawerPanel from "../components/ConversationDrawerPanel.jsx";
import ConversationInboxList from "../components/ConversationInboxList.jsx";
import ConversationSyncStatusBar from "../components/ConversationSyncStatusBar.jsx";
import { useConversationList } from "../hooks/useConversationList.js";
import { useConversationPin } from "../hooks/useConversationPin.js";
import { isReadyForLeadConversion } from "../utils/conversationLeadStatus.js";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const STATUS_OPTIONS = [
  { label: "Open inbox", value: "open" },
  { label: "All", value: "all" },
  { label: "Closed", value: "closed" },
  { label: "Archived", value: "archived" },
];

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const ConversationsView = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("open");
  const [source, setSource] = useState("");
  const [since, setSince] = useState(null);
  const [needsAttention, setNeedsAttention] = useState(false);
  const [readyToConvert, setReadyToConvert] = useState(false);

  const [drawerRow, setDrawerRow] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const listFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      status,
      source: source || undefined,
      since: since ? since.toISOString() : undefined,
      needsAttention,
      page: pagination.page,
      pageSize: pagination.pageSize,
    }),
    [
      debouncedSearch,
      status,
      source,
      since,
      needsAttention,
      pagination.page,
      pagination.pageSize,
    ]
  );

  const {
    data: listData,
    isLoading,
    isFetching,
    syncStatus,
    cachedAt,
    refresh,
  } = useConversationList(listFilters);

  const { togglePin, pinningChannelId } = useConversationPin();

  const conversations = Array.isArray(listData?.conversations)
    ? listData.conversations
    : [];
  const listPagination = {
    page: listData?.pagination?.page ?? pagination.page,
    pageSize: listData?.pagination?.pageSize ?? pagination.pageSize,
    total: listData?.pagination?.total ?? 0,
  };
  const n8nError = listData?.n8nError ?? null;
  const usingFixtures = listData?.fixture === true;
  const listLoadFailed = syncStatus?.variant === "error" && conversations.length === 0;

  const invalidateConversationList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["conversation-list"] });
  }, [queryClient]);

  useEffect(() => {
    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  }, [debouncedSearch, status, source, since, needsAttention]);

  const handleResetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setStatus("open");
    setSource("");
    setSince(null);
    setNeedsAttention(false);
    setReadyToConvert(false);
  };

  const filteredConversations = useMemo(() => {
    let rows = conversations;

    if (readyToConvert) {
      rows = rows.filter(isReadyForLeadConversion);
    }

    return [...rows].sort((a, b) => {
      const aPinned = a.isPinned ? 1 : 0;
      const bPinned = b.isPinned ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;
      if (aPinned && bPinned && a.pinOrder !== b.pinOrder) {
        return (a.pinOrder ?? 0) - (b.pinOrder ?? 0);
      }

      const aReady = isReadyForLeadConversion(a) ? 1 : 0;
      const bReady = isReadyForLeadConversion(b) ? 1 : 0;
      if (aReady !== bReady) return bReady - aReady;
      const aTime = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
      const bTime = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [conversations, readyToConvert]);

  const openConversation = useCallback((record) => {
    if (!record) return;
    setDrawerRow(record);
  }, []);

  const handleOpenLead = useCallback(
    (record) => {
      if (!record?.leadId) return;
      navigate(`/leads/${record.leadId}`);
    },
    [navigate]
  );

  const handleConversationClosed = useCallback(() => {
    invalidateConversationList();
  }, [invalidateConversationList]);

  const handleHandoffChange = useCallback(() => {
    invalidateConversationList();
  }, [invalidateConversationList]);

  const handlePageChange = useCallback((page, pageSize) => {
    setPagination({ page, pageSize });
  }, []);

  const handleRefreshList = useCallback(() => {
    refresh();
  }, [refresh]);

  return (
    <Flex vertical gap={16} style={{ padding: isMobile ? 12 : 24 }}>
      <Flex align="center" justify="space-between" gap={12} wrap="wrap">
        <Flex align="center" gap={8}>
          <MessageOutlined style={{ color: "#005249", fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>
            Conversations
          </Title>
        </Flex>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefreshList}
          loading={isFetching}
        >
          Refresh
        </Button>
      </Flex>

      <ConversationSyncStatusBar
        syncStatus={syncStatus}
        cachedAt={cachedAt}
        onRetry={handleRefreshList}
        compact={isMobile}
      />

      <Text type="secondary">
        Browse Paul ↔ customer WhatsApp conversations. Open any row for the full
        chat. Linked rows also offer Open lead. Closed threads are hidden unless
        you search or filter by Closed.
      </Text>

      <Flex gap={12} wrap="wrap" align="center">
        <Input
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, phone, reference, message…"
          prefix={<SearchOutlined />}
          style={{ width: isMobile ? "100%" : 300 }}
        />
        <Select
          value={status}
          options={STATUS_OPTIONS}
          onChange={setStatus}
          style={{ width: 140 }}
        />
        <Input
          allowClear
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="Source"
          style={{ width: 150 }}
        />
        <DatePicker
          value={since}
          onChange={setSince}
          placeholder="Since"
          style={{ width: 160 }}
        />
        <Flex align="center" gap={8}>
          <Switch checked={needsAttention} onChange={setNeedsAttention} />
          <Text>Needs attention</Text>
        </Flex>
        <Flex align="center" gap={8}>
          <Switch checked={readyToConvert} onChange={setReadyToConvert} />
          <Text>Ready for lead</Text>
        </Flex>
        <Button onClick={handleResetFilters}>Reset</Button>
      </Flex>

      {listLoadFailed && (
        <Alert
          type="error"
          showIcon
          message={syncStatus?.message || "Failed to load conversations"}
          action={
            <Button size="small" onClick={handleRefreshList}>
              Retry
            </Button>
          }
        />
      )}

      {usingFixtures && (
        <Alert
          type="info"
          showIcon
          message="Using local conversation fixtures"
          description="n8n list webhook is not configured. Run: node backend/scripts/seed-conversation-test-data.js to link the Antoinette fixture row to a lead."
        />
      )}

      {n8nError && (
        <Alert
          type="warning"
          showIcon
          message={`n8n conversation list unavailable (${n8nError.code}${
            n8nError.httpStatus != null ? ` · HTTP ${n8nError.httpStatus}` : ""
          })`}
          description={
            n8nError.hint ||
            "Configure N8N_CONVERSATION_LIST_WEBHOOK_URL and the conversation summary secret, or check n8n workflow logs."
          }
        />
      )}

      <ConversationInboxList
        conversations={filteredConversations}
        loading={isLoading && conversations.length === 0}
        pagination={listPagination}
        onOpen={openConversation}
        onOpenLead={handleOpenLead}
        onTogglePin={togglePin}
        pinningChannelId={pinningChannelId}
        onPageChange={handlePageChange}
      />

      <Drawer
        title={
          drawerRow ? (
            <Flex vertical gap={0} style={{ minWidth: 0, lineHeight: 1.3 }}>
              <Text strong ellipsis style={{ fontSize: 15 }}>
                {drawerRow.customerName || drawerRow.customerPhone}
              </Text>
              {drawerRow.customerName && (
                <Text type="secondary" ellipsis style={{ fontSize: 12, fontWeight: 400 }}>
                  {drawerRow.customerPhone}
                </Text>
              )}
            </Flex>
          ) : null
        }
        placement="right"
        width={isMobile ? "100%" : "min(720px, 60vw)"}
        open={Boolean(drawerRow)}
        onClose={() => setDrawerRow(null)}
        destroyOnClose
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
          body: {
            padding: isMobile ? 8 : 16,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            flex: 1,
            minHeight: 0,
          },
        }}
      >
        {drawerRow && (
          <ConversationDrawerPanel
            phone={drawerRow.customerPhone}
            customerName={drawerRow.customerName}
            referenceNumber={drawerRow.referenceNumber}
            leadId={drawerRow.leadId}
            inboxStatus={drawerRow.inboxStatus}
            assignedTo={drawerRow.assignedTo}
            handoffStatus={drawerRow.handoffStatus}
            draftReadyToPromote={drawerRow.draftReadyToPromote}
            draftStatus={drawerRow.draftStatus}
            onClose={() => setDrawerRow(null)}
            onClosed={handleConversationClosed}
            onHandoffChange={handleHandoffChange}
          />
        )}
      </Drawer>
    </Flex>
  );
};

export default ConversationsView;
